import { View, useWindowDimensions, Image, ImageSourcePropType, ScrollView, TouchableOpacity, Pressable, RefreshControl, StyleSheet, } from 'react-native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import CommonHeader from '../../shared/commonComponents/commonHeader'
import Animated, {
    useSharedValue,
    withTiming,
    Easing,
    useAnimatedStyle,
    useAnimatedReaction,
} from 'react-native-reanimated';

// Keep your existing imports
import { ZenCalendar, ZenIcon, ZenText, ZenToolTip } from 'zen-core-elements'
import { PieChart } from 'react-native-gifted-charts'
import { PopoverPlacement } from 'react-native-popover-view'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import CommonService from '../../shared/services/commonService'
import { useAppNavigation } from '../../types/@react-navigation/navigation'

import { getDashboardSummary, getStoreDataReport, getStoreRevenueAnalytics, getStoreSetupStatus, setDashboardFilter } from '../../redux/slices/dashboard.slice'
import { DashboardProgressDataType } from './dashboard.module'
import { config } from '../../shared/environment'
import moment from 'moment'
import { CustomModal } from '../../shared/commonComponents/customModal'
import { IconType } from 'zen-core-elements/src/elements/zenIcon/zenIcon'
import ZenStepIndicator from 'zen-core-elements/src/elements/zenStepIndicator/zenStepIndicator'
import { ThemeConsumer } from '@rneui/themed'
import DashboardSkeleton from './dashboardSkeleton'
import { appColors, fontSize } from '../../themes/appTheme'
import { appFonts } from 'zen-core-elements/src/shared/constants';
import { runOnJS } from 'react-native-worklets';

// --- INTERFACES (Kept intact) ---
interface RevenueFilterDataType {
    id: number;
    name: string;
    value: { startDate: string | null; endDate: string | null; };
    dateModalVisible?: boolean;
}

export interface DashboardData {
    netSaleAmount: number;
    totalOrderCount: number;
    soldProductCount: number;
    netPaymentAmount: number;
    activeOfferCount: number;
    expiredOfferCount: number;
    totalTaxAmount: number;
    staffCount: number;
    customerCount: number;
    purchasedGiftCardCount: number;
    issuedGiftCardCount: number;
    registerClosureCount: number;
}

export interface CardConfig {
    title: string;
    subtitle?: string;
    icon?: ImageSourcePropType;
    iconType?: IconType;
    iconName?: string;
    iconColor?: string;
    getValue: (data: DashboardData) => number;
    formatValue?: (val: number) => string;
    navigation?: string;
    horizontal?: boolean;
    // New prop for styling distinct cards
    variant?: 'primary' | 'secondary' | 'accent';
}

export type CardConfigKey = 'revenue' | 'orders' | 'products' | 'payments' | 'activeOffers' | 'expiredOffers' | 'totalTax' | 'staff' | 'customer';
export type CardConfigsMap = Record<CardConfigKey, CardConfig>;

interface TypewriterProps {
    text: string;
    durationPerChar?: number;
}

const TypewriterText = React.memo(({ text, durationPerChar = 500 }: TypewriterProps) => {
    const [displayText, setDisplayText] = useState('');
    const typingProgress = useSharedValue(0);

    useEffect(() => {
        typingProgress.value = 0;
        setDisplayText('');
        typingProgress.value = withTiming(text.length, {
            duration: text.length * durationPerChar,
            easing: Easing.out(Easing.quad),
        });
    }, [text, durationPerChar]);

    useAnimatedReaction(
        () => Math.floor(typingProgress.value),
        (nextCharIndex, prevCharIndex) => {
            if (nextCharIndex !== prevCharIndex) {
                runOnJS(setDisplayText)(text.slice(0, nextCharIndex));
            }
        },
        [text]
    );

    const animatedCursorStyle = useAnimatedStyle(() => {
        const isTyping = typingProgress.value < text.length;
        return {
            opacity: isTyping ? 1 : 0,
        };
    });

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <ZenText style={styles.typingText} content={displayText} />
            <Animated.View style={[styles.cursor, animatedCursorStyle]} />
        </View>
    );
});

/**
 * Calculates rounded percentages that sum to exactly 100% using the Largest Remainder Method.
 */
const getRoundedPercentages = (values: number[]): number[] => {
    const total = values.reduce((sum, val) => sum + val, 0);
    if (total === 0) {
        return values?.map(() => 0);
    }

    const percentages = values?.map(val => (val / total) * 100);
    const rounded = percentages?.map(p => Math.floor(p));
    const sumRounded = rounded?.reduce((sum, val) => sum + val, 0);
    const diff = 100 - sumRounded;

    if (diff === 0) {
        return rounded;
    }

    const remainders = percentages?.map((p, idx) => ({
        index: idx,
        remainder: p - Math.floor(p)
    }));

    // Sort by remainder descending, then index ascending for a stable sort
    remainders.sort((a, b) => {
        if (Math.abs(b?.remainder - a?.remainder) > 1e-9) {
            return b?.remainder - a?.remainder;
        }
        return a?.index - b?.index;
    });

    for (let i = 0; i < diff; i++) {
        rounded[remainders[i]?.index] += 1;
    }

    return rounded;
};

const DashboardScreen = () => {
    const navigation = useAppNavigation()
    const { width, height } = useWindowDimensions()
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState(true)
    const [pauseApi, setPauseApi] = useState(false);
    const [refreshing, setRefreshing] = useState(false)
    const { revenueAnalytics, dashboardSummary, storeSetupStatus, currentFilter, storeData, dashboardCache } = useAppSelector(state => state.Dashboard)
    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'

    const scrollRef = useRef<ScrollView>(null)
    const userName = useAppSelector((state) => state.common.currentUser.value?.firstName) || ''
    const [visibleFilter, setVisibleFilter] = useState(false)
    const [customModal, setCustomModal] = useState(false);

    // --- FILTERS ---
    const initialRevenueFilters = [
        { id: 1, name: "Today", value: { startDate: moment().format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD") } },
        { id: 2, name: "Yesterday", value: { startDate: moment().subtract(1, "days").format("YYYY-MM-DD"), endDate: moment().subtract(1, "days").format("YYYY-MM-DD") } },
        { id: 3, name: "Last 7 Days", value: { startDate: moment().subtract(7, "days").format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD") } },
        { id: 4, name: "This Month", value: { startDate: moment().startOf("month").format("YYYY-MM-DD"), endDate: moment().endOf("month").format("YYYY-MM-DD") } },
        { id: 6, name: "Lifetime", value: { startDate: null, endDate: moment().format("YYYY-MM-DD") } },
        { id: 7, name: "Custom Range", value: { startDate: null, endDate: null } }
    ];

    const [revenueFilter, setRevenueFilter] = useState<RevenueFilterDataType[]>(initialRevenueFilters);
    const [selectedId, setSelectedId] = useState(1);
    const selectedFilter = useMemo(() => revenueFilter?.find((f) => f.id === selectedId), [revenueFilter, selectedId]);
    const isLandscape = width > height;
    const [visualStep, setVisualStep] = useState(0);
    const [finalStep, setFinalStep] = useState(0);
    //Width available inside the progress card, used to spread the steps evenly
    const [progressWidth, setProgressWidth] = useState(0);

    // --- DATA CALCS ---
    const revenueTotal = useMemo(() => {
        const details = revenueAnalytics?.value?.revenueAnalytics?.revenueDetails;
        if (!details) return 0;
        return (details.discount || 0) + (details.netSale || 0) + (details.refund || 0) + (details.tax || 0) + (details.totalSales || 0);
    }, [revenueAnalytics])

    const pieData = useMemo(() => {
        const details = revenueAnalytics?.value?.revenueAnalytics?.revenueDetails;
        const netSale = details?.netSale ?? 0;
        const tax = details?.tax ?? 0;
        const discount = details?.discount ?? 0;
        const refund = details?.refund ?? 0;
        const totalSales = details?.totalSales ?? 0;

        const values = [netSale, tax, discount, refund, totalSales];
        const percentages = getRoundedPercentages(values);

        return [
            { value: netSale, color: appColors.primary, name: 'Net Sales', percentage: percentages[0] },
            { value: tax, color: '#FFB547', name: 'Tax', percentage: percentages[1] },
            { value: discount, color: '#05CD99', name: 'Discounts', percentage: percentages[2] },
            { value: refund, color: '#E31A1A', name: 'Refunds', percentage: percentages[3] },
            { value: totalSales, color: '#FF7700', name: 'Total Sales', percentage: percentages[4] },
        ];
    }, [revenueAnalytics, revenueTotal]);

    // --- CONFIG WITH COLORS ---
    const CARD_CONFIGS: CardConfigsMap = {
        revenue: {
            title: 'Total Revenue',
            icon: require('../../assets/Dashboard/Revenues.png'),
            getValue: (data) => data?.netSaleAmount ?? 0,
            formatValue: (val) => CommonService.currencyStandard(currencySymbol, String(val || 0)),
            navigation: 'RevenueReport'
        },
        orders: {
            title: 'Total Orders',
            icon: require('../../assets/Dashboard/Order.png'),
            getValue: (data) => data?.totalOrderCount ?? 0,
            navigation: 'OrderReport'
        },
        products: {
            title: 'Products Sold',
            icon: require('../../assets/Dashboard/Product.png'),
            getValue: (data) => data?.soldProductCount ?? 0,
            navigation: 'ProductReport'
        },
        payments: {
            title: 'Payments',
            icon: require('../../assets/Dashboard/Revenue.png'),
            getValue: (data) => data?.netPaymentAmount ?? 0,
            formatValue: (val) => CommonService.currencyStandard(currencySymbol, String(val || 0)),
            navigation: 'PaymentReport'
        },
        activeOffers: { title: 'Active', iconName: 'local-offer', iconType: 'material', iconColor: '#05CD99', getValue: (data) => data?.activeOfferCount ?? 0, navigation: 'OfferReport' },
        expiredOffers: { title: 'Expired', iconName: 'timer-off', iconType: 'material-community', iconColor: '#FFB547', getValue: (data) => data?.expiredOfferCount ?? 0, navigation: 'OfferReport' },
        totalTax: { title: 'Tax Amount', iconName: 'receipt', iconType: 'ionicon', iconColor: appColors.primary, getValue: (data) => data?.totalTaxAmount ?? 0, formatValue: (val) => CommonService.currencyStandard(currencySymbol, String(val || 0)), navigation: 'TaxReport' },
        staff: { title: 'Staff Count', iconName: 'people', iconType: 'ionicon', iconColor: '#2B3674', getValue: (data) => data?.staffCount ?? 0, navigation: 'StaffReport' },
        customer: { title: 'Customer Count', iconName: 'people', iconType: 'ionicon', iconColor: '#4318FF', getValue: (data) => data?.customerCount ?? 0, navigation: 'CustomerReport' },
    };

    // --- API CALLS ---
    useEffect(() => {
        if (!storeData?.value?.settings?.createdAt) return;

        setRevenueFilter(prev =>
            prev.map(f =>
                f.id === 6
                    ? { ...f, value: { startDate: moment(storeData?.value?.settings?.createdAt).format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD"), } }
                    : f
            )
        );
    }, [storeData?.value?.settings?.createdAt]);

    useEffect(() => {
        if (pauseApi || !selectedFilter) return;
        getAllPosDashboardData(true)
    }, [selectedFilter, pauseApi]);

    const onPressFilter = (item: RevenueFilterDataType) => {
        setVisibleFilter(false)
        if (item.id === 7) {
            setPauseApi(true);
            setCustomModal(true);
            return;
        }
        const filterKey = JSON.stringify({ customDateRange: { begin: item?.value?.startDate, end: item?.value?.endDate! } });
        if (!dashboardCache[filterKey]?.revenueAnalytics || !dashboardCache[filterKey]?.dashboardSummary) {
            setIsLoading(true);
        }
        setRevenueFilter(prev => prev?.map(f => f.id === 7 ? { ...f, value: { startDate: null, endDate: null } } : f))
        setSelectedId(item.id);
    };

    const onRefresh = () => {
        setRefreshing(true);
        if (selectedId === 1) {
            getAllPosDashboardData(true)
        } else {
            setSelectedId(1)
        }
        // getAllPosDashboardData(true)
    };

    const onSelectCustomRange = (range: { startDate: string; endDate: string }) => {
        if (!range.startDate || !range.endDate) return;
        setRevenueFilter((prev) => prev?.map((f) => f.id === 7 ? { ...f, value: range } : f));
        const filterKey = JSON.stringify({ customDateRange: { begin: range.startDate, end: range.endDate } });
        if (!dashboardCache[filterKey]?.revenueAnalytics || !dashboardCache[filterKey]?.dashboardSummary) {
            setIsLoading(true);
        }
        setSelectedId(7);
        setCustomModal(false);
        setPauseApi(false);
    };

    const getAllPosDashboardData = async (forceRefresh = false) => {
        const filterData = JSON.stringify({ customDateRange: { begin: selectedFilter?.value?.startDate, end: selectedFilter?.value?.endDate! } })

        if (!forceRefresh && dashboardSummary?.value && revenueAnalytics?.value && storeSetupStatus?.value && currentFilter === filterData) {
            setIsLoading(false)
            setRefreshing(false)
            return;
        }

        if (storeData?.value && storeSetupStatus?.value) {
            await dispatch(getDashboardSummary({ storeId: config.storeId, data: { filterData }, forceRefresh })).unwrap().then(async (dashboardSummaryData) => {
                if (dashboardSummaryData?.success) {
                    await dispatch(getStoreRevenueAnalytics({ storeId: config.storeId, data: { filterData: filterData }, forceRefresh })).unwrap().then(async (revenueAnalyticsData) => {
                        if (revenueAnalyticsData?.success) {
                            setRefreshing(false);
                        }
                    }).catch((revenueAnalyicsErr) => {
                        console.log("Error in fetch revenue analytics data", revenueAnalyicsErr?.message);
                    }).finally(() => {
                        setIsLoading(false);
                        setRefreshing(false);
                    })
                }
            })
                .catch((dashboardSummaryErr) => {
                    setIsLoading(false);
                    setRefreshing(false);
                    console.log("Error in fetch dashboard summary data", dashboardSummaryErr?.message);
                })
        } else {
            await dispatch(getStoreDataReport()).unwrap().then(async (storeDataResponse) => {
                if (storeDataResponse?.success) {
                    await dispatch(getDashboardSummary({ storeId: config.storeId, data: { filterData }, forceRefresh })).unwrap().then(async (dashboardSummaryData) => {

                        if (dashboardSummaryData?.success) {
                            await dispatch(getStoreRevenueAnalytics({ storeId: config.storeId, data: { filterData }, forceRefresh })).unwrap().then(async (revenueAnalyticsData) => {
                                if (revenueAnalyticsData?.success) {
                                    await dispatch(getStoreSetupStatus()).unwrap().then((response) => {
                                        if (response?.success) {
                                            dispatch(setDashboardFilter(filterData))
                                        }
                                    }).catch((err) => {
                                        console.log("Error in fetch store setup status data", err?.message);
                                    })
                                        .finally(() => {
                                            setIsLoading(false);
                                            setRefreshing(false);
                                        })
                                }
                            }).catch((revenueAnalyicsErr) => {
                                console.log("Error in fetch revenue analytics data", revenueAnalyicsErr?.message);
                            })
                        }
                    })
                        .catch((dashboardSummaryErr) => {
                            console.log("Error in fetch dashboard summary data", dashboardSummaryErr?.message);
                        })
                }
            }).catch(err => {
                console.log("Error in fetch dashboard repor data", err?.message);
            })
        }
    }

    const handleNavigation = (route: string) => {
        if (navigation && route) navigation.navigate(route as any, { beginDate: selectedFilter?.value?.startDate!, endDate: selectedFilter?.value?.endDate! });
    };


    // --- UI COMPONENTS ---

    // 1. Elegant Metric Card (Row Layout)
    const MetricCard = ({ config, data, onPress, style }: { config: CardConfig, data: DashboardData | undefined, onPress?: () => void, style?: object }) => {
        const value = config.getValue(data!);
        const formattedValue = config.formatValue ? config.formatValue(value) : value;

        return (
            <TouchableOpacity onPress={onPress} activeOpacity={0.8} style={[styles.card, style, { minWidth: width > height ? 160 : 175 }]}>
                <View style={[styles.iconBox, { backgroundColor: appColors.lightBackgroundColor }]}>
                    <Image source={config.icon} style={styles.iconImg} />
                </View>
                <View style={styles.cardTextContainer}>
                    <ZenText content={config.title} size={fontSize.badgeFontSize} color={appColors.labelColor} bold />
                    <ZenText content={formattedValue.toString()} size={fontSize.smallerFontSize} color={appColors.dark} bold style={{ marginTop: 2 }} />
                </View>
            </TouchableOpacity>
        );
    };

    // 2. Mini List Item (For Offers/Store Details)
    const MiniListItem = ({ config, data }: { config: CardConfig, data: DashboardData | undefined }) => {
        const value = config.getValue(data!);
        const formattedValue = config.formatValue ? config.formatValue(value) : value;

        return (
            <TouchableOpacity onPress={() => handleNavigation(config.navigation!)} style={styles.listItem}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={styles.miniIconBox}>
                        <ZenIcon name={config.iconName!} type={config.iconType!} size={22} color={config.iconColor} />
                    </View>
                    <ZenText content={config.title} size={fontSize?.errorFontSize} color={appColors.dark} style={{ marginLeft: 12 }} bold />
                </View>
                <ZenText content={formattedValue.toString()} size={fontSize?.errorFontSize} color={appColors.dark} bold />
            </TouchableOpacity>
        )
    }


    // --- PROGRESS LOGIC ---
    const progressData: DashboardProgressDataType[] = useMemo(() => {
        if (!storeSetupStatus?.value?.success || !storeSetupStatus?.value?.dashboardDetails) return []

        return [
            { id: '1', name: 'Create account', icnName: 'add', isEnable: storeSetupStatus?.value?.dashboardDetails?.createAccount, navigation: null, "message": { reaction: 'great!', val: 'Created Account', } },
            { id: '1', name: 'Add location(s)', icnName: 'add-location', isEnable: storeSetupStatus?.value?.dashboardDetails?.addLocation, navigation: { stack: "SettingStack", screen: "LocationStack", params: { screen: "AddEditLocation" } }, "message": { reaction: 'nice', val: 'Added Location' } },
            { id: '1', name: 'Add product(s)', icnName: 'add-shopping-cart', isEnable: storeSetupStatus?.value?.dashboardDetails?.addYourProduct, navigation: { stack: "ProductStack", screen: "AddEditProduct" }, "message": { reaction: 'good', val: 'Added Products' } },
            { id: '1', name: 'Set your payment mode', icnName: 'payment', isEnable: storeSetupStatus?.value?.dashboardDetails?.paymentMode, navigation: null, "message": { reaction: 'wonderful', val: 'Added Payment' } },
            // { id: '1', name: 'Connect supported hardware', icnName: 'print', isEnable: storeSetupStatus?.value?.dashboardDetails?.supportedHardware, navigation: { stack: "BarCodeScannerStack", screen: "AddBarCodeScanner" }, "message": { reaction: 'Excellent', val: 'Added Hardware' } }
        ]
    }, [storeSetupStatus])

    useEffect(() => {
        if (progressData && progressData.length > 0) {
            let targetIndex = progressData.findIndex(item => !item.isEnable);
            if (targetIndex === -1) targetIndex = progressData.length;
            setFinalStep(targetIndex);
        }
    }, [progressData]);

    // Steps fill the card width; they only scroll when there are too many to fit
    const minStepWidth = isLandscape ? 200 : 160;
    const stepperWidth = Math.max(progressWidth, progressData.length * minStepWidth);
    const isProgressScrollable = stepperWidth > progressWidth;
    const completedSteps = progressData.filter((step) => step.isEnable).length;

    useEffect(() => {
        if (progressData.length === 0) return;
        if (visualStep < finalStep) {
            const timer = setTimeout(() => {
                setVisualStep(visualStep + 1)
                if (isProgressScrollable && scrollRef?.current) {
                    scrollRef?.current?.scrollTo({ x: visualStep * (stepperWidth / progressData.length), animated: true });
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [visualStep, finalStep, progressData, isProgressScrollable, stepperWidth]);

    const renderStepIndicator = ({ position, stepStatus }: any) => {
        const step = progressData[position];
        if (!step) return null;

        // Based on "visualStep", the library decides status
        const isFinished = stepStatus === 'finished';
        const isCurrent = stepStatus === 'current';
        const isTouchable = position < finalStep;

        const handleNavigation = () => {
            if (step.navigation) {
                if (step.navigation?.params) {
                    navigation.navigate(step?.navigation?.stack as any, { screen: step?.navigation?.screen, params: step?.navigation?.params });
                } else {
                    navigation.navigate(step?.navigation?.stack as any, { screen: step?.navigation?.screen });
                }
            }
        }

        // 1. Finished Steps = Checkmark
        if (isFinished) {
            return (
                <View style={styles.stepFinishedContainer}>
                    <ZenIcon type='material' name="check" size={22} color={appColors.light} style={styles.stepIndicatorIcon} />
                </View>
            );
        }

        // 2. Current Step = Specific Icon (e.g. Printer) in Blue/Primary
        if (isCurrent) {
            return (
                <TouchableOpacity onPress={handleNavigation} disabled={isTouchable} style={styles.stepIndicatorActive}>
                    <ZenIcon name={step.icnName} type='material' size={22} color={appColors.primary} style={styles.stepIndicatorIcon} />
                </TouchableOpacity>
            );
        }
        // 3. Future Steps = Specific Icon in Grey
        return (
            <TouchableOpacity onPress={handleNavigation} disabled={isTouchable} style={styles.stepIndicatorUnfinished}>
                <ZenIcon name={step.icnName} type='material' size={22} color={appColors.lightGrey} style={styles.stepIndicatorIcon} />
            </TouchableOpacity>
        );
    };

    const renderLabel = ({ position, stepStatus }: any) => {
        const step = progressData[position];
        if (!step) return null;
        const isFinished = stepStatus === 'finished';
        const isCurrent = stepStatus === 'current';

        return (
            <View style={[styles.stepCardContainer, isCurrent && styles.activeStepCard]}>
                <ZenText content={isFinished ? step?.message?.val : step.name} bold style={styles.stepTitle} size={fontSize.errorFontSize} />
                {isFinished && (
                    <ZenText content={step?.message?.reaction} bold color="#05CD99" size={fontSize.badgeFontSize} style={styles.stepReaction} />
                )}
            </View>
        );
    };

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={styles.container}>
                    {/* Header */}
                    <CommonHeader
                        headerName=""
                        leftComponent={
                            <View style={{ flexDirection: "row", alignItems: "center", gap: 15 }}>
                                {width <= 900 && (
                                    <TouchableOpacity onPress={() => navigation.openDrawer()}>
                                        <ZenIcon name={'dots-grid'} type={"material-community"} size={width > 600 ? 28 : 22} color={theme?.dark || appColors.dark} />
                                    </TouchableOpacity>
                                )}
                                <TypewriterText text={`Hello, ${userName || 'User'}!`} />
                            </View>
                        }
                        rightComponent={
                            <ZenToolTip
                                isVisible={visibleFilter}
                                onRequestClose={() => setVisibleFilter(false)}
                                popoverStyle={styles.popover}
                                height={100}
                                width={100}
                                placement={PopoverPlacement.BOTTOM}
                                popover={<Pressable style={styles.filterBtn} onPress={() => setVisibleFilter(true)}>
                                    <ZenIcon name="calendar-month" type="material-community" size={22} color={"#4318FF"} style={{ marginRight: 8 }} />
                                    <ZenText content={selectedFilter?.name === "Custom Range" ? selectedFilter?.value?.startDate + " - " + selectedFilter?.value?.endDate : selectedFilter?.name || "Filter"} size={fontSize.errorFontSize} color={"#4318FF"} bold />
                                </Pressable>}
                                content={
                                    <View style={{ padding: 5 }}>
                                        {revenueFilter?.map(f => (
                                            <TouchableOpacity key={f.id} style={styles.filterItem} onPress={() => onPressFilter(f)}>
                                                <ZenText content={f.name} color={f.id === selectedId ? "#4318FF" : '#555'} size={fontSize?.errorFontSize} />
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                }
                            />
                        }
                    />
                    {isLoading ? <DashboardSkeleton /> : <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.content} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}>

                        {/* 1. KEY METRICS ROW (Uniform Grid) */}
                        <View style={styles.metricsRow}>
                            <MetricCard config={CARD_CONFIGS.revenue} data={dashboardSummary?.value?.dashboardDetails} style={styles.metricCardItem} onPress={() => handleNavigation('RevenueReport')} />
                            <MetricCard config={CARD_CONFIGS.orders} data={dashboardSummary?.value?.dashboardDetails} style={styles.metricCardItem} onPress={() => handleNavigation('OrderReport')} />
                            <MetricCard config={CARD_CONFIGS.products} data={dashboardSummary?.value?.dashboardDetails} style={styles.metricCardItem} onPress={() => handleNavigation('ProductReport')} />
                            <MetricCard config={CARD_CONFIGS.payments} data={dashboardSummary?.value?.dashboardDetails} style={styles.metricCardItem} onPress={() => handleNavigation('PaymentReport')} />
                        </View>

                        {/* 2. SPLIT SECTION (Chart + Lists) */}
                        <View style={[styles.splitSection, { flexDirection: isLandscape ? 'row' : 'column' }]}>
                            {/* Left: Chart */}
                            <View style={[styles.card, { flex: isLandscape ? 0.65 : 1 }]}>
                                <View style={styles.chartHeader}>
                                    <ZenText content="Revenue Distribution" size={16} color={appColors.dark} bold />
                                    <View style={styles.chartTag}><ZenText content="This Period" size={10} color={appColors.primary} bold /></View>
                                </View>

                                {revenueTotal > 0 ? (
                                    <View style={styles.chartBody}>
                                        <PieChart
                                            data={pieData}
                                            donut
                                            radius={isLandscape ? 120 : 90}
                                            innerRadius={isLandscape ? 90 : 70}
                                            innerCircleColor="#FFF"
                                            centerLabelComponent={() => (
                                                <View style={{ alignItems: 'center' }}>
                                                    <ZenText content="Sales" size={fontSize?.smallHeaderFontSize} color={appColors.labelColor} />
                                                    <ZenText content={CommonService.currencyStandard(currencySymbol, String(revenueTotal ?? 0))} size={fontSize?.smallHeaderFontSize} color={appColors.dark} bold />
                                                </View>
                                            )}
                                        />
                                        <View style={{ marginLeft: isLandscape ? 20 : 10 }}>
                                            {pieData.map((p, i) => (
                                                <View key={i} style={styles.legendItem}>
                                                    <View style={[styles.dot, { backgroundColor: p.color }]} />
                                                    <ZenText content={`${p?.name}`} size={fontSize?.errorFontSize} color={appColors.labelColor} style={{ width: 90, textAlign: "left" }} />
                                                    <ZenText content={`${p?.percentage}%`} size={fontSize?.errorFontSize} color={appColors.dark} bold />
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                ) : (
                                    <View style={theme?.dashboardStyles.noDataContainer}>
                                        <Image height={50} width={50} resizeMode='contain' source={require("../../assets/no_data_image.webp")} />
                                        <ZenText content="Oops! No Data Found" bold color={appColors.dark} />
                                        <ZenText style={theme?.dashboardStyles.noDataText} size={theme?.tinyFontSize} content="No revenue has been generated due to the absence of sales! Revenue will be recorded once a sale is made." color={appColors.lightDark} />
                                    </View>
                                )}
                            </View>

                            {/* Right: Lists (Stacked) */}
                            <View style={{ flex: isLandscape ? 0.35 : 1, marginLeft: isLandscape ? 15 : 0, marginTop: isLandscape ? 0 : 20 }}>

                                {/* Offers Status */}
                                <View style={[styles.card, { marginBottom: 20 }]}>
                                    <ZenText content="Offers Status" size={14} color={appColors.dark} bold style={{ marginBottom: 10 }} />
                                    <MiniListItem config={CARD_CONFIGS.activeOffers} data={dashboardSummary?.value?.dashboardDetails} />
                                    <View style={styles.divider} />
                                    <MiniListItem config={CARD_CONFIGS.expiredOffers} data={dashboardSummary?.value?.dashboardDetails} />
                                </View>


                                {/* Store Details */}
                                <View style={styles.card}>
                                    <ZenText content="Store Overview" size={14} color={appColors.dark} bold style={{ marginBottom: 10 }} />
                                    <MiniListItem config={CARD_CONFIGS.staff} data={dashboardSummary?.value?.dashboardDetails} />
                                    <View style={styles.divider} />
                                    <MiniListItem config={CARD_CONFIGS.customer} data={dashboardSummary?.value?.dashboardDetails} />
                                    <View style={styles.divider} />
                                    <MiniListItem config={CARD_CONFIGS.totalTax} data={dashboardSummary?.value?.dashboardDetails} />
                                </View>

                            </View>

                        </View>

                        {/* Progress bar */}
                        {progressData.length ? <View style={styles.progressSection}>
                            <View style={styles.progressHeader}>
                                <View style={{ flex: 1 }}>
                                    <ZenText content={completedSteps === progressData.length ? "Your store is all set!" : "Complete your store setup"} size={14} color={appColors.dark} bold />
                                    <ZenText content={`${completedSteps} of ${progressData.length} steps completed`} size={fontSize.badgeFontSize} color={appColors.labelColor} style={{ marginTop: 2 }} />
                                </View>
                                {/* <View style={styles.progressTrack}>
                                    <View style={[styles.progressFill, { width: `${(completedSteps / progressData.length) * 100}%` }]} />
                                </View> */}
                            </View>
                            <View onLayout={(event) => setProgressWidth(Math.round(event.nativeEvent.layout.width))}>
                                {progressWidth ? <ScrollView horizontal showsHorizontalScrollIndicator={false} ref={scrollRef} scrollEnabled={isProgressScrollable}>
                                    <View style={{ width: stepperWidth }}>
                                        <ZenStepIndicator
                                            customStyles={{
                                                stepIndicatorSize: 50,
                                                currentStepIndicatorSize: 50,
                                                separatorStrokeWidth: 4,
                                                currentStepStrokeWidth: 0,
                                                stepStrokeCurrentColor: 'transparent',
                                                stepStrokeWidth: 0,
                                                stepStrokeFinishedColor: 'transparent',
                                                stepStrokeUnFinishedColor: 'transparent',
                                                separatorFinishedColor: '#05CD99',
                                                separatorUnFinishedColor: '#E9EDF7',
                                                stepIndicatorFinishedColor: 'transparent',
                                                stepIndicatorUnFinishedColor: 'transparent',
                                                stepIndicatorCurrentColor: 'transparent',
                                                stepIndicatorLabelFontSize: 0,
                                                currentStepIndicatorLabelFontSize: 0,
                                                labelColor: 'transparent',
                                                labelSize: 0,
                                            }}
                                            currentPosition={visualStep}
                                            stepCount={progressData.length}
                                            renderStepIndicator={renderStepIndicator}
                                            renderLabel={renderLabel}
                                            direction="horizontal"
                                            labels={progressData.map(x => x.name)}
                                        />
                                    </View>
                                </ScrollView> : null}
                            </View>
                        </View> : null}
                    </ScrollView>}

                    <CustomModal visible={customModal} transparent animationType="fade">
                        <View style={styles.modalOverlay}>
                            <Pressable style={StyleSheet.absoluteFill} onPress={() => setCustomModal(false)} />
                            <View style={styles.modalBody}>
                                <TouchableOpacity onPress={() => { setCustomModal(false); setPauseApi(false); }} style={styles.closeBtn}>
                                    <ZenIcon name="close" type="material" size={20} />
                                </TouchableOpacity>
                                <ZenCalendar rangeuser={revenueFilter?.find((f) => f.id === 7)?.value} setUserRange={onSelectCustomRange} />
                            </View>
                        </View>
                    </CustomModal>
                </View>
            )}
        </ThemeConsumer>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: appColors.lightBackgroundColor,
    },
    content: {
        padding: 15,
        paddingBottom: 25
    },
    // Header
    filterBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        borderColor: "#4318FF",
        borderWidth: 1,
        backgroundColor: appColors.light,
        paddingHorizontal: 15,
        paddingVertical: 8,
        borderRadius: 20,
        elevation: 2,
        shadowColor: '#b0bec5',
        shadowOpacity: 0.2,
        shadowOffset: { width: 0, height: 2 },
    },
    popover: {
        borderRadius: 12,
        backgroundColor: appColors.light,
        width: 150
    },
    filterItem: {
        padding: 10,
        borderBottomWidth: 0.5,
        borderBottomColor: '#f0f0f0'
    },

    // Metrics
    metricsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginBottom: 10,
    },
    metricCardItem: {
        width: '23.5%', // 4 items in a row with spacing
        marginBottom: 10
    },

    // Cards
    card: {
        backgroundColor: appColors.light,
        borderRadius: 20,
        padding: 15,
        elevation: 5, // Android shadow
        shadowColor: '#7090B0', // Soft blueish shadow
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 10,

    },
    iconBox: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 15,
    },
    iconImg: {
        width: 28,
        height: 28,
        resizeMode: 'contain'
    },
    cardTextContainer: {
        justifyContent: 'center'
    },

    // Split Section
    splitSection: {
        justifyContent: 'space-between',
    },
    chartHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20
    },
    chartTag: {
        backgroundColor: appColors.lightBackgroundColor,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8
    },
    chartBody: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        opacity: 0.5
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
        gap: 5
    },
    dot: {
        width: 12,
        height: 12,
        borderRadius: 7,
        marginRight: 8
    },

    // Mini Lists
    listItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    miniIconBox: {
        width: 32,
        height: 32,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center'
    },
    divider: {
        height: 1,
        backgroundColor: appColors.lightBackgroundColor,
        marginVertical: 7
    },

    // Modals
    modalOverlay: {
        flex: 1,
        backgroundColor: appColors.modalBg100,
        justifyContent: 'center',
        alignItems: 'center'
    },
    modalBody: {
        backgroundColor: appColors.light,
        padding: 20,
        borderRadius: 20,
        width: '50%',
        minWidth: 320
    },
    closeBtn: {
        alignSelf: 'flex-end',
        marginBottom: 10,
        padding: 5
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.7)',
        justifyContent: 'center',
        alignItems: 'center'
    },
    // Progress Section
    progressSection: {
        marginTop: 20,
        paddingTop: 15,
        paddingBottom: 10,
        backgroundColor: appColors.light,
        borderRadius: 20,
        elevation: 5,
        shadowColor: '#7090B0',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
    },
    progressHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 15,
        paddingHorizontal: 15,
        marginBottom: 10
    },
    progressTrack: {
        width: 120,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#E9EDF7',
        overflow: 'hidden'
    },
    progressFill: {
        height: '100%',
        borderRadius: 4,
        backgroundColor: '#05CD99'
    },
    stepFinishedContainer: {
        width: 50,
        height: 50,
        borderRadius: 25,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#05CD99'
    },
    stepIndicatorActive: {
        width: 50,
        height: 50,
        borderRadius: 25,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#E6FAF5',
        borderWidth: 2,
        borderColor: '#05CD99'
    },
    stepIndicatorUnfinished: {
        width: 50,
        height: 50,
        borderRadius: 25,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: appColors.light,
        borderWidth: 1,
        borderColor: '#E9EDF7'
    },
    stepIndicatorIcon: {
        alignSelf: 'center'
    },
    stepCardContainer: {
        // marginTop: 10,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 10,
        paddingBottom: 0
    },
    activeStepCard: {
        // active style
    },
    stepTitle: {
        color: '#2B3674',
        textAlign: 'center'
    },
    stepReaction: {
        marginTop: 4
    },
    typingText: {
        fontSize: fontSize?.headerFontSize,
        fontFamily: appFonts?.bold,
        color: appColors?.dark,
    },
    cursor: { width: 3, height: 24, backgroundColor: '#333', marginLeft: 2 }
});

export default DashboardScreen;